import { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation } from "react-router-dom";

import { AGENTIC_AI_ROUTE } from "@/components/agenticAI/constants";
import type { AppDispatch, RootState } from "@/store";
import {
  buildAnalysisConfigSnapshot,
  resetAnalysisConfigSlices,
  restoreAnalysisConfigSnapshot
} from "@/store/analysisConfigSnapshot";
import {
  clearAnalysisConfigSnapshot,
  parkAnalysisConfig,
  selectHasParkedAnalysisConfig,
  selectParkedAnalysisConfig
} from "@/store/slice/analysisConfigSnapshotSlice";
import { selectIsOnboardingActive } from "@/store/slice/onboardingSlice";

/**
 * Gives one workspace to Agentic AI and one workspace to manual Spatial
 * Analysis. The two routes use the same analysis slices. Thus without this hook
 * the datasets of one mode go with the user into the other mode.
 *
 * `ProtectedRoute` mounts the hook one time, above the outlet. The hook uses the
 * path. Thus it operates for each entry and each exit: a nav item, the back and
 * the forward button, a bookmark, and a page refresh.
 */
export const useAgenticWorkspaceSwap = (isAuthenticated: boolean) => {
  const dispatch = useDispatch<AppDispatch>();
  // Read the store directly, in the effect only. The snapshot needs the six
  // analysis slices at the moment of the mode change. A subscription to them would
  // render `ProtectedRoute` again at each change, with no advantage.
  const store = useStore<RootState>();
  const { pathname } = useLocation();

  const hasParkedWorkspace = useSelector(selectHasParkedAnalysisConfig);
  const isOnboardingActive = useSelector(selectIsOnboardingActive);

  const isAgenticRoute = pathname.startsWith(AGENTIC_AI_ROUTE);
  const hasHandledPageLoadRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    const isPageLoad = !hasHandledPageLoadRef.current;
    hasHandledPageLoadRef.current = true;

    if (isAgenticRoute && !hasParkedWorkspace) {
      dispatch(
        parkAnalysisConfig(buildAnalysisConfigSnapshot(store.getState()))
      );
      resetAnalysisConfigSlices(dispatch);
      return;
    }

    if (isAgenticRoute && isPageLoad && !isOnboardingActive) {
      resetAnalysisConfigSlices(dispatch);
      return;
    }

    if (!isAgenticRoute && hasParkedWorkspace && !isOnboardingActive) {
      const parkedManualSnapshot = selectParkedAnalysisConfig(store.getState());

      // Reset first, thus the datasets of the agent do not go into manual mode.
      resetAnalysisConfigSlices(dispatch);
      if (parkedManualSnapshot) {
        restoreAnalysisConfigSnapshot(dispatch, parkedManualSnapshot);
      }
      dispatch(clearAnalysisConfigSnapshot());
    }
  }, [
    isAuthenticated,
    isAgenticRoute,
    hasParkedWorkspace,
    isOnboardingActive,
    dispatch,
    store
  ]);
};
